import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import BugForm from '../../../../_components/accounts/tester/bugs/BugForm';
import { Notice, PageContainer, PageSection } from '../../../../ui';
import { BugSeverity } from '../../../../types/BugSeverity';
import { OperationStatus } from '../../../../types/OperationStatus';
import { CycleStatus } from '../../../../types/CycleStatus';
import actions from '../../../../redux/actions';
import { getApprovedDevicePrettyName } from '../../../../services/deviceService';
import config from '../../../../config';
import * as utils from '../../../../util/utils';
import moment from 'moment';

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    cycles: state.cycles,
    devices: state.devices,
    browsers: state.browsers,
    newBug: state.newBug,
    createBugStatus: state.createBugStatus,
    device: state.resolution.device,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    createBug: (cycleId: number, bugData: any) => dispatch(actions.createBug(cycleId, bugData)),
    updateNewBug: (newBug: any) => dispatch(actions.updateNewBug(newBug)),
  };
};

class NewBug extends Component<any> {
  public state: any;

  constructor(props: any) {
    super(props);

    this.state = {
      // Opened from a failed test plan step (`newbug?testPlan=true`): keep the step prefilled in the store
      isTestPlanBug: new URLSearchParams(props.location.search).get('testPlan') === 'true',
      newBug: {
        ...props.newBug,
      },
      cycleId: parseInt(props.match.params.id, 10),
      errors: new Set(),
      totalFilesSize: 0,
    };
  }

  componentDidMount() {
    if (!this.state.isTestPlanBug) {
      this.props.updateNewBug({
        testPlanStepId: null,
        topic: '',
        stepsToReproduce: '',
        expectedResult: '',
        actualResult: '',
        comment: '',
        severity: BugSeverity.Low,
        imageFiles: [],
        videoFiles: [],
      });
    }
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.createBugStatus.status !== this.props.createBugStatus.status) {
      if (this.props.createBugStatus.status === OperationStatus.success) {
        this.props.history.push(`/account/tester/cycle/${this.state.cycleId}/bugs`);
      }
    }
  }

  resetErrorState = (event: any) => {
    const name = event.target.name;
    const errors = new Set(this.state.errors);

    if (errors.has(name)) {
      errors.delete(name);
      this.setState({ errors });
    }
  };

  onInput = (event: any, data: any) => {
    const lengthRestrictions = {
      topic: 500,
      stepsToReproduce: 5000,
      expectedResult: 5000,
      actualResult: 5000,
      comment: 5000,
    };

    const newBug = { ...this.state.newBug };

    if (lengthRestrictions[data.name] && data.value.length > lengthRestrictions[data.name]) {
      newBug[data.name] = data.value.substring(0, lengthRestrictions[data.name] - 1);
    } else {
      newBug[data.name] = data.value;
    }

    this.setState({ newBug });
  };

  validateInput = (event: any, data: any) => {
    const errors = new Set(this.state.errors);
    const name = data && data.name ? data.name : event.target.name;
    const value = data && data.value ? data.value : event.target.value;

    if (!value || !value.length) {
      errors.add(name);
    } else {
      errors.delete(name);
    }

    this.setState({ errors });
  };

  onImageUpload = (acceptedImageFiles: any, rejectedImageFiles: any) => {
    const imageFiles = [...this.state.newBug.imageFiles];

    const totalImagesFileSize = imageFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    const totalVideoFileSize = this.state.newBug.videoFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    let totalFilesSize = totalImagesFileSize + totalVideoFileSize;

    for (let file of acceptedImageFiles) {
      totalFilesSize += file.size;

      const isImage = utils.isImage(file.name);
      const ext = utils.getFileExtension(file.name);

      file = Object.assign(file, {
        preview: isImage ? URL.createObjectURL(file) : null,
        isImage,
        lowercaseName: file.name.toLowerCase(),
        ext,
      });

      imageFiles.push(file);
    }

    this.setState({
      newBug: {
        ...this.state.newBug,
        imageFiles,
      },
      totalFilesSize,
    });
  };

  onUndoImageUpload = (event, data) => {
    event.stopPropagation();

    const imageFiles = [...this.state.newBug.imageFiles].filter(
      (el, i) => i !== parseInt(data['data-index'], 10)
    );

    const totalImagesFileSize = imageFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    const totalVideoFileSize = this.state.newBug.videoFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    let totalFilesSize = totalImagesFileSize + totalVideoFileSize;

    this.setState({
      newBug: {
        ...this.state.newBug,
        imageFiles,
      },
      totalFilesSize,
    });
  };

  onVideoUpload = (acceptedVideoFiles) => {
    const videoFiles = [...this.state.newBug.videoFiles];

    const totalImagesFileSize = this.state.newBug.imageFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    const totalVideoFileSize = videoFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    let totalFilesSize = totalImagesFileSize + totalVideoFileSize;

    for (let file of acceptedVideoFiles) {
      totalFilesSize += file.size;

      file = Object.assign(file, {
        preview: URL.createObjectURL(file),
        lowercaseName: file.name.toLowerCase(),
      });

      videoFiles.push(file);
    }

    this.setState({
      newBug: {
        ...this.state.newBug,
        videoFiles,
      },
      totalFilesSize,
    });
  };

  onUndoVideoUpload = (event, data) => {
    event.stopPropagation();

    const videoFiles = [...this.state.newBug.videoFiles].filter(
      (el, i) => i !== data['data-index']
    );

    const totalImagesFileSize = this.state.newBug.imageFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    const totalVideoFileSize = videoFiles.reduce((result: number, file: any) => {
      result += file.size;
      return result;
    }, 0);

    let totalFilesSize = totalImagesFileSize + totalVideoFileSize;

    this.setState({
      newBug: {
        ...this.state.newBug,
        videoFiles,
      },
      totalFilesSize,
    });
  };

  onSubmitBug = () => {
    const newBug = this.state.newBug;

    const bugProps: Record<string, any> = {
      topic: newBug.topic,
      stepsToReproduce: newBug.stepsToReproduce,
      expectedResult: newBug.expectedResult,
      actualResult: newBug.actualResult,
      comment: newBug.comment,
      severity: newBug.severity,
    };

    if (newBug.testPlanStepId) {
      bugProps.testPlanStepId = newBug.testPlanStepId;
    }

    const bugData = {
      bugProps,
      files: {
        imageFiles: newBug.imageFiles,
        videoFiles: newBug.videoFiles,
      },
    };

    this.props.createBug(this.state.cycleId, bugData);
  };

  isSaveEnabled = () => {
    const mandatoryFields = [
      'topic',
      'stepsToReproduce',
      'expectedResult',
      'actualResult',
      'severity',
    ];

    for (const el of mandatoryFields) {
      if (!this.state.newBug[el] || !this.state.newBug[el].length) {
        return false;
      }
    }

    if (this.state.errors.size) {
      return false;
    }

    if (this.props.createBugStatus.status === OperationStatus.processing) {
      return false;
    }

    return true;
  };

  render() {
    if (!this.props.cycles.length) {
      return null;
    }

    const cycle = this.props.cycles.find((el) => el.id === this.state.cycleId);

    const isCycleOver = ( cycle.status === CycleStatus.Completed && moment(cycle.questionsTime).isBefore(moment()) );

    const newBug = this.state.newBug;

    const approvedDeviceName = getApprovedDevicePrettyName(cycle.approvedDevice);

    const bugCreateProcessing = this.props.createBugStatus.status === OperationStatus.processing;
    const bugWithStep = newBug.testPlanStepId ? true : false;

    const bugSaveEnabled = this.isSaveEnabled();

    const totalFilesSizeMB =
      this.state.totalFilesSize === 0 ? 0 : (this.state.totalFilesSize / 1024 / 1024).toFixed(2);

    const maxAttachmentsSizeMB = config.maxAttachmentsSizeMB;

    const attachmentsAreTooHeavy = totalFilesSizeMB > maxAttachmentsSizeMB;

    const isMobile = this.props.device === 'mobile' || this.props.device === 'mobile-tablet';

    return (
      <PageContainer isMobile={isMobile}>
        {cycle.status === CycleStatus.Completed && (
          <Notice title="This cycle has finished">
            <Link to="/account/tester">Go to dashboard</Link>
          </Notice>
        )}
        <PageSection title="New bug" padding={isMobile ? 1.5 : 2.5}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2.5 }}>
            Device: {approvedDeviceName}
          </Typography>
          <BugForm
            values={newBug}
            errors={this.state.errors}
            disabled={isCycleOver}
            stepFieldsLocked={bugWithStep}
            onChange={(name, value) => this.onInput(null, { name, value })}
            onFocus={(name) => this.resetErrorState({ target: { name } })}
            onBlur={(name, value) => this.validateInput({ target: { name, value } }, { name, value })}
            imageFiles={newBug.imageFiles}
            videoFiles={newBug.videoFiles}
            onAddImages={(files) => this.onImageUpload(files, [])}
            onAddVideos={(files) => this.onVideoUpload(files)}
            onRemoveImage={(index) => this.onUndoImageUpload({ stopPropagation() {} }, { 'data-index': index })}
            onRemoveVideo={(index) => this.onUndoVideoUpload({ stopPropagation() {} }, { 'data-index': index })}
            totalFilesSizeMB={totalFilesSizeMB}
            maxAttachmentsSizeMB={maxAttachmentsSizeMB}
            attachmentsTooHeavy={attachmentsAreTooHeavy}
            submitLabel="Create"
            submitDisabled={!bugSaveEnabled || attachmentsAreTooHeavy || isCycleOver}
            submitting={bugCreateProcessing}
            onSubmit={this.onSubmitBug}
          />
        </PageSection>
      </PageContainer>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NewBug));
